import React, { useMemo, useState } from "react";
import { TrendingUp, Activity, Target, Award } from "lucide-react";

/**
 * Minimal, premium SVG line chart — no extra deps.
 *  - Three series: attendance %, goals, assists (toggle pills)
 *  - Smooth bezier curve, area fill, dot + tooltip on hover
 *  Props: data = [{ label, attendance_pct, goals, assists, trainings, matches }, ...]
 */
const SERIES = [
  { key: "attendance_pct", label: "Attendance %", suffix: "%", color: "#3B82F6", icon: Activity },
  { key: "goals",          label: "Goals",        suffix: "",  color: "#FFB300", icon: Target },
  { key: "assists",        label: "Assists",      suffix: "",  color: "#42A5F5", icon: Award },
];

export default function PlayerProgressChart({ data = [], lang = "en", className = "" }) {
  const [active, setActive] = useState("attendance_pct");
  const [hover, setHover] = useState(null);

  const W = 720;
  const H = 200;
  const PAD = { l: 32, r: 16, t: 20, b: 28 };
  const innerW = W - PAD.l - PAD.r;
  const innerH = H - PAD.t - PAD.b;

  const series = SERIES.find((s) => s.key === active) || SERIES[0];

  const values = data.map((d) => Number(d[active]) || 0);
  const maxV = Math.max(1, ...values, active === "attendance_pct" ? 100 : 5);
  const stepX = data.length > 1 ? innerW / (data.length - 1) : 0;

  const points = data.map((d, i) => ({
    x: PAD.l + i * stepX,
    y: PAD.t + innerH - (Math.min(maxV, Number(d[active]) || 0) / maxV) * innerH,
    v: Number(d[active]) || 0,
    label: d.label,
  }));

  // Smooth path via cubic catmull-rom-to-bezier
  const path = useMemo(() => {
    if (points.length === 0) return "";
    if (points.length === 1) return `M${points[0].x},${points[0].y}`;
    let d = `M ${points[0].x},${points[0].y}`;
    for (let i = 0; i < points.length - 1; i++) {
      const p0 = points[i - 1] || points[i];
      const p1 = points[i];
      const p2 = points[i + 1];
      const p3 = points[i + 2] || p2;
      const c1x = p1.x + (p2.x - p0.x) / 6;
      const c1y = p1.y + (p2.y - p0.y) / 6;
      const c2x = p2.x - (p3.x - p1.x) / 6;
      const c2y = p2.y - (p3.y - p1.y) / 6;
      d += ` C ${c1x},${c1y} ${c2x},${c2y} ${p2.x},${p2.y}`;
    }
    return d;
  }, [points]);

  const areaPath = useMemo(() => {
    if (points.length === 0) return "";
    const last = points[points.length - 1];
    const first = points[0];
    return `${path} L ${last.x},${PAD.t + innerH} L ${first.x},${PAD.t + innerH} Z`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, points]);

  return (
    <div className={`card-sharp p-4 md:p-6 ${className}`} data-testid="progress-chart">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="label-mono mb-1 flex items-center gap-2">
            <TrendingUp className="w-3 h-3 text-pitch" />
            {lang === "bg" ? "// Прогрес" : "// Progress"}
          </div>
          <div className="font-oswald uppercase text-xl tracking-tight">
            {lang === "bg" ? "Седмични показатели" : "Weekly performance"}
          </div>
        </div>
        <div className="inline-flex border border-white/10 p-0.5 rounded-sm">
          {SERIES.map((s) => {
            const isActive = s.key === active;
            const Icon = s.icon;
            const label = lang === "bg"
              ? { attendance_pct: "Посещаемост", goals: "Голове", assists: "Асистенции" }[s.key]
              : s.label.replace(" %", "");
            return (
              <button
                key={s.key}
                onClick={() => setActive(s.key)}
                className={`px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest flex items-center gap-1.5 transition-colors ${isActive ? "bg-pitch/15 text-pitch" : "text-white/45 hover:text-white"}`}
                data-testid={`progress-tab-${s.key}`}
                style={isActive ? { boxShadow: `inset 0 0 0 1px ${s.color}30` } : {}}
              >
                <Icon className="w-3 h-3" /> {label}
              </button>
            );
          })}
        </div>
      </div>

      {data.length === 0 ? (
        <div className="h-[200px] flex items-center justify-center text-white/30 text-xs font-mono tracking-widest">
          {lang === "bg" ? "Няма данни още" : "No data yet"}
        </div>
      ) : (
        <div className="relative overflow-x-auto">
          <svg width="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" className="block">
            <defs>
              <linearGradient id="pp-area" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%"  stopColor={series.color} stopOpacity="0.35" />
                <stop offset="100%" stopColor={series.color} stopOpacity="0" />
              </linearGradient>
            </defs>
            {/* Y gridlines (4) */}
            {[0, 0.25, 0.5, 0.75, 1].map((p, i) => (
              <line key={i} x1={PAD.l} x2={W - PAD.r} y1={PAD.t + innerH * p} y2={PAD.t + innerH * p}
                    stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
            ))}
            {/* Area + Line */}
            <path d={areaPath} fill="url(#pp-area)" />
            <path d={path} fill="none" stroke={series.color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
            {/* Dots */}
            {points.map((p, i) => (
              <g key={i}
                 onMouseEnter={() => setHover({ ...p, i })}
                 onMouseLeave={() => setHover(null)}>
                <circle cx={p.x} cy={p.y} r="3.5" fill="#0A1628" stroke={series.color} strokeWidth="2" />
                <rect x={p.x - stepX / 2} y={PAD.t} width={Math.max(stepX, 14)} height={innerH} fill="transparent" />
              </g>
            ))}
            {/* X labels */}
            {points.map((p, i) => (
              <text key={`l-${i}`} x={p.x} y={H - 8} textAnchor="middle"
                    style={{ fontSize: 10, fill: "rgba(255,255,255,0.4)", fontFamily: "monospace" }}>
                {p.label}
              </text>
            ))}
            {hover && (
              <g pointerEvents="none">
                <line x1={hover.x} x2={hover.x} y1={PAD.t} y2={PAD.t + innerH}
                      stroke={series.color} strokeOpacity="0.35" strokeDasharray="3 3" />
                <circle cx={hover.x} cy={hover.y} r="5" fill={series.color} />
              </g>
            )}
          </svg>
          {hover && (
            <div
              className="absolute -translate-x-1/2 pointer-events-none"
              style={{ left: `${(hover.x / W) * 100}%`, top: -8 }}
            >
              <div className="bg-[#0A1628]/95 border border-pitch/40 px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest text-pitch backdrop-blur-md rounded-sm">
                {hover.v}{series.suffix} · <span className="text-white/55">{hover.label}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
